import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { CountrySelectorSimple } from '@cotyorg/ccx-organism-country-selector';
import withRedux from '../withRedux';
import ObserverBar from '../Observer';
import HeaderNav from '../HeaderNav';
import HeaderNavToggle from '../HeaderNavToggle';
import HeaderActions from '../HeaderActions';
import HeaderLogo from '../HeaderLogo';

const generateHeaderCssClass = (fixed, themeName, isPinned, isNavigationVisible) =>
  classNames(
    'header',
    'header-two-row', {
      'header--fixed': fixed,
      [`header--theme-${themeName}`]: themeName && !isPinned && !isNavigationVisible,
    },
  );

const generateLogoCssClass = logoPosition =>
  classNames(
    'cell',
    'shrink',
    'align-middle',
    'flex-container',
    'header-two-row__logo', {
      'xsmall-order-1': logoPosition === 'left',
    },
  );

const generateCountrySelectCssClass = logoPosition => classNames(
  'cell',
  'align-middle',
  'flex-container',
  'header__country-selector', {
    'xsmall-order-2 shrink': logoPosition === 'left',
    auto: logoPosition !== 'left',
  },
);

const generateActionsCssClass = logoPosition =>
  classNames(
    'cell',
    'auto',
    'align-right',
    'align-middle',
    'flex-container',
    'header-two-row__actions', {
      'xsmall-order-2': logoPosition === 'left',
    },
  );

const generateNavCssClass = logoPosition =>
  classNames(
    'align-middle',
    'header__navigation',
    'header-two-row__navigation',
    'flex-container', {
      'xsmall-order-2 shrink': logoPosition === 'left',
      'align-center': logoPosition !== 'left',
    },
  );

const generateMobileNavCssClass = logoPosition =>
  classNames(
    'cell',
    'align-middle',
    'flex-container',
    'header__mobile-navigation',
    'auto', {
      'xsmall-order-3 shrink': logoPosition === 'left',
    },
  );

const HeaderTwoRow = ({
  themeName,
  showSearch,
  showMiniCart,
  showCountrySelector = false,
  logoSrc,
  logoAlt = '',
  fixed,
  isPinned,
  setHeaderFixed,
  logoPosition,
  navType,
  mobileNavType,
  isNavigationVisible,
  searchLink,
  cartLink,
  toggleSearchVisibility,
  searchIconSrc,
  miniCartIconSrc,
  chevronIconSrc,
  leftChevronIconSrc,
  rightChevronIconSrc,
  closeIconSrc,
  hamburgerIconSrc,
  content: {
    mobileNavButtonOpenText,
    mobileNavButtonCloseText,
    searchButtonText,
    logoLinkText,
    iconLinkImage,
    iconLinkUrl,
  } = {},
  homeLink,
  cartQuantity,
  miniCart,
  toggleMiniCart,
  currentBreakpoint,
  onMouseEnter,
  onMouseLeave,
  logoWidth,
  logoHeight,
  hamburgerIconWidth = 20,
  hamburgerIconHeight,
  searchIconWidth = 20,
  searchIconHeight,
  miniCartIconWidth = 32,
  miniCartIconHeight,
}) => (
  <Fragment>
    <ObserverBar setHeaderFixed={setHeaderFixed} fixed={fixed} />

    <header className={generateHeaderCssClass(fixed, themeName, isPinned, isNavigationVisible)}>
      <div className="grid-container">
        <div className="grid-x grid-padding-x">
          <HeaderNavToggle
            navType={mobileNavType}
            navCssClass={generateMobileNavCssClass(logoPosition)}
            leftChevronIconSrc={leftChevronIconSrc}
            rightChevronIconSrc={rightChevronIconSrc}
            closeIconSrc={closeIconSrc}
            hamburgerIconSrc={hamburgerIconSrc}
            hamburgerIconWidth={hamburgerIconWidth}
            hamburgerIconHeight={hamburgerIconHeight}
            mobileNavButtonOpenText={mobileNavButtonOpenText}
            mobileNavButtonCloseText={mobileNavButtonCloseText}
          />

          <div className={generateCountrySelectCssClass(logoPosition)}>
            {showCountrySelector && (
              <CountrySelectorSimple
                chevronIconSrc={chevronIconSrc}
              />
            )}
          </div>

          <HeaderLogo
            logoCssClass={generateLogoCssClass(logoPosition)}
            logoSrc={logoSrc}
            logoAlt={logoAlt}
            logoLinkText={logoLinkText}
            homeLink={homeLink}
            logoWidth={logoWidth}
            logoHeight={logoHeight}
          />

          <div className={generateActionsCssClass(logoPosition)}>
            <HeaderActions
              showSearch={showSearch}
              showMiniCart={showMiniCart}
              cartQuantity={cartQuantity}
              searchLink={searchLink}
              cartLink={cartLink}
              onSearchClick={toggleSearchVisibility}
              searchIconSrc={searchIconSrc}
              miniCartIconSrc={miniCartIconSrc}
              searchButtonText={searchButtonText}
              miniCart={miniCart}
              toggleMiniCart={toggleMiniCart}
              currentBreakpoint={currentBreakpoint}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
              searchIconWidth={searchIconWidth}
              searchIconHeight={searchIconHeight}
              miniCartIconWidth={miniCartIconWidth}
              miniCartIconHeight={miniCartIconHeight}
              iconLinkUrl={iconLinkUrl}
              iconLinkImage={iconLinkImage}
            />
          </div>
        </div>
      </div>

      <div className="header-two-row__navigation-wrapper">
        <div className="grid-container">
          <div className="grid-x grid-padding-x">
            <div className="cell">
              <HeaderNav navType={navType} navCssClass={generateNavCssClass(logoPosition)} />
            </div>
          </div>
        </div>
      </div>
    </header>
  </Fragment>
);

HeaderTwoRow.propTypes = {
  showSearch: PropTypes.bool,
  showMiniCart: PropTypes.bool,
  showCountrySelector: PropTypes.bool,
  fixed: PropTypes.bool,
  logoSrc: PropTypes.string.isRequired,
  logoAlt: PropTypes.string,
  themeName: PropTypes.oneOf(['filled', 'transparent']),
  logoPosition: PropTypes.oneOf(['left', 'center']),
  navType: PropTypes.string,
  mobileNavType: PropTypes.string,
  isPinned: PropTypes.bool,
  setHeaderFixed: PropTypes.func,
  isNavigationVisible: PropTypes.bool,
  toggleSearchVisibility: PropTypes.func,
  searchLink: PropTypes.string,
  cartLink: PropTypes.string,
  searchIconSrc: PropTypes.string,
  miniCartIconSrc: PropTypes.string,
  chevronIconSrc: PropTypes.string,
  leftChevronIconSrc: PropTypes.string,
  rightChevronIconSrc: PropTypes.string,
  closeIconSrc: PropTypes.string,
  hamburgerIconSrc: PropTypes.string,
  content: PropTypes.shape({
    mobileNavButtonOpenText: PropTypes.string,
    mobileNavButtonCloseText: PropTypes.string,
    searchButtonText: PropTypes.string,
    logoLinkText: PropTypes.string,
    iconLinkImage: PropTypes.string,
    iconLinkUrl: PropTypes.string,
  }).isRequired,
  homeLink: PropTypes.string,
  cartQuantity: PropTypes.number,
  miniCart: PropTypes.func,
  toggleMiniCart: PropTypes.func,
  currentBreakpoint: PropTypes.string,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  logoWidth: PropTypes.number,
  logoHeight: PropTypes.number,
  hamburgerIconWidth: PropTypes.number,
  hamburgerIconHeight: PropTypes.number,
  searchIconWidth: PropTypes.number,
  searchIconHeight: PropTypes.number,
  miniCartIconWidth: PropTypes.number,
  miniCartIconHeight: PropTypes.number,
};

export default withRedux(HeaderTwoRow);
